/**
 * The season timeline — the forecast, on one chronological spine (§8).
 *
 * Every date on this screen was decided by the server: `GET /timeline` runs the
 * projection engine and returns occurrences, certs and inspections already
 * placed. What happens here is presentation — interleaving them, labelling a
 * crossing as firm or estimated, and applying the user's before/during choice,
 * which is the one thing the server has no opinion about (lib/schedule).
 *
 * The engine's own vocabulary carries straight through: occurrence #1 is firm,
 * later ones are estimates of estimates — each assumes the service before it
 * actually happened — and they render dashed to say so (§4).
 */

import { useState } from 'react';
import { CircleDashed, DollarSign, Gauge, Pencil, Plus, Repeat, TriangleAlert, Wrench } from 'lucide-react';

import type {
  EventDto,
  HistoryEvent,
  HistoryResponse,
  HistoryService,
  TimelineOccurrence,
  TimelineResponse,
} from '@svcdue/contracts';

import { useCars, useHistory, useMarkChecked, useTimeline } from '../api/hooks';
import { Empty, QueryState } from '../components/states';
import { Eyebrow } from '../components/primitives';
import { InspectCard } from '../components/InspectCard';
import { useGarageContext, usePermissions } from '../garage/GarageProvider';
import { useSchedule } from '../lib/schedule';
import { daysUntil, fmtShort, today } from '../lib/format';
import { toBlocks } from '../lib/display';

/** The two faces of the timeline: the forecast forward, the record backward. */
export type TimelineMode = 'forecast' | 'history';

type CarOption = { id: string; name: string };

export function Timeline({
  openAddEvent,
  openEditEvent,
}: {
  openAddEvent: () => void;
  openEditEvent: (event: EventDto) => void;
}) {
  const { garageId } = useGarageContext();
  const cars = useCars(garageId);
  const carOptions = (cars.data ?? []).map((c) => ({ id: c.id, name: c.nickname }));

  /**
   * The Forecast/History switch and the per-car filter live here, above both
   * views, so flipping tabs keeps the car you were looking at — the two are
   * different pictures of the *same* car, and losing the filter on every switch
   * would make comparing them a chore.
   */
  const [mode, setMode] = useState<TimelineMode>('forecast');
  const [filter, setFilter] = useState<string>('all');

  // Each mode owns its own data hook, so the History fetch only fires once the
  // tab is opened rather than on every timeline visit. Mounting one or the other
  // (not one component calling both) keeps that lazy without breaking hook rules.
  return mode === 'history' ? (
    <HistoryScreen garageId={garageId} mode={mode} onMode={setMode} cars={carOptions} filter={filter} onFilter={setFilter} />
  ) : (
    <ForecastScreen
      garageId={garageId}
      mode={mode}
      onMode={setMode}
      cars={carOptions}
      filter={filter}
      onFilter={setFilter}
      openAddEvent={openAddEvent}
      openEditEvent={openEditEvent}
    />
  );
}

function ForecastScreen({
  garageId,
  mode,
  onMode,
  cars,
  filter,
  onFilter,
  openAddEvent,
  openEditEvent,
}: {
  garageId: string | undefined;
  mode: TimelineMode;
  onMode: (mode: TimelineMode) => void;
  cars: CarOption[];
  filter: string;
  onFilter: (filter: string) => void;
  openAddEvent: () => void;
  openEditEvent: (event: EventDto) => void;
}) {
  /**
   * Filtering happens client-side, over one unfiltered fetch.
   *
   * `GET /timeline?carId=` would filter server-side, but flipping the filter is
   * a UI gesture on data already in hand — refetching per click would put a
   * round trip behind a toggle and, worse, make the filter a loading state.
   */
  const query = useTimeline(garageId);

  return (
    <QueryState query={query}>
      {(data) => (
        <TimelineView
          data={data}
          garageId={garageId!}
          mode={mode}
          onMode={onMode}
          filter={filter}
          onFilter={onFilter}
          cars={cars}
          onAddEvent={openAddEvent}
          onEditEvent={openEditEvent}
        />
      )}
    </QueryState>
  );
}

/** The Forecast/History switch — two pictures of the same fleet, one spine each. */
function TimelineTabs({ mode, onMode }: { mode: TimelineMode; onMode: (mode: TimelineMode) => void }) {
  return (
    <div className="tl-tabs" role="tablist" aria-label="Timeline view">
      <button
        role="tab"
        aria-selected={mode === 'forecast'}
        className={'tl-tab' + (mode === 'forecast' ? ' tl-tab-on' : '')}
        onClick={() => onMode('forecast')}
      >
        Forecast
      </button>
      <button
        role="tab"
        aria-selected={mode === 'history'}
        className={'tl-tab' + (mode === 'history' ? ' tl-tab-on' : '')}
        onClick={() => onMode('history')}
      >
        History
      </button>
    </div>
  );
}

/** The per-car filter chips, shared by both views. */
function CarFilter({ cars, filter, onFilter }: { cars: CarOption[]; filter: string; onFilter: (filter: string) => void }) {
  if (cars.length === 0) return null;
  return (
    <div className="tlf">
      <button className={'tlf-btn' + (filter === 'all' ? ' tlf-on' : '')} onClick={() => onFilter('all')}>
        All
      </button>
      {cars.map((c) => (
        <button key={c.id} className={'tlf-btn' + (filter === c.id ? ' tlf-on' : '')} onClick={() => onFilter(c.id)}>
          {c.name}
        </button>
      ))}
    </div>
  );
}

export function TimelineView({
  data,
  garageId,
  mode,
  onMode,
  filter,
  onFilter,
  cars,
  onAddEvent,
  onEditEvent,
}: {
  data: TimelineResponse;
  garageId: string;
  /** The tabs render only when wired — a test can drop them and the rest stands. */
  mode?: TimelineMode;
  onMode?: (mode: TimelineMode) => void;
  filter: string;
  onFilter: (filter: string) => void;
  cars: CarOption[];
  /** Optional so a test can render the view without wiring the modal. */
  onAddEvent?: () => void;
  onEditEvent?: (event: EventDto) => void;
}) {
  const { canLogMaintenance } = usePermissions();
  const { planFor } = useSchedule();
  const markChecked = useMarkChecked(garageId);

  const blocks = toBlocks(data).filter((b) => filter === 'all' || b.carId === filter);
  // "Next up" is the first *upcoming* event, not merely the first one on the
  // spine — a past event that was never converted to actual still sits at the
  // top of the timeline, and badging it "Next up" is plainly wrong.
  const firstEventIdx = blocks.findIndex((b) => b.kind === 'event' && b.date >= today());
  const overdue = data.occurrences.filter((o) => o.overdue && (filter === 'all' || o.carId === filter));

  const inspections = data.inspections.filter((i) => filter === 'all' || i.carId === filter);

  return (
    <div className="screen">
      {onMode ? <TimelineTabs mode={mode ?? 'forecast'} onMode={onMode} /> : <Eyebrow>Forecast</Eyebrow>}
      <div className="row-h">
        <h1 className="h1">Season timeline</h1>
        {canLogMaintenance && onAddEvent && (
          <button className="link" onClick={onAddEvent}>
            <Plus size={13} /> Add event
          </button>
        )}
      </div>
      <p className="lede">
        Each event's estimated <b className="c-ink">passes</b> and <b className="c-ink">miles</b> feed the forecast:
        service lands where the interval actually crosses, and repeats <b className="c-amber">skip events</b> that don't
        add up — shown dashed, since each assumes you serviced at the tile before it.
      </p>

      <CarFilter cars={cars} filter={filter} onFilter={onFilter} />

      <div className="legend">
        <span>
          <span className="lg-flag" /> Race event
        </span>
        <span>
          <span className="lg-tick tick-proj" /> Next service — firm
        </span>
        <span>
          <span className="lg-dash" /> Repeat — estimated
        </span>
        <span>
          <span className="lg-tick tick-cert" /> Date-certain
        </span>
      </div>

      {/* Overdue is the one thing that earns interrupting the spine — it is not
          a forecast, it is a service that was already missed. */}
      {overdue.map((o) => (
        <div className="over-banner" key={o.itemId}>
          <TriangleAlert size={15} />
          <div>
            <b>
              {o.itemName} · {o.who}
            </b>
            <span>Overdue since {fmtShort(o.date)} — every {o.label}</span>
          </div>
          <span className="over-tag">Overdue now</span>
        </div>
      ))}

      {blocks.length === 0 ? (
        <Empty
          title="Nothing on the timeline yet"
          hint="Add a planned event and some parts with check conditions, and the forecast fills in."
          action={
            canLogMaintenance && onAddEvent ? (
              <button className="act" onClick={onAddEvent}>
                <Plus size={12} /> Add event
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="tl">
          {blocks.map((b, i) => {
            const when = (
              <div className="tl-when">
                <b>{fmtShort(b.date)}</b>
                <span>{daysUntil(b.date)}d</span>
              </div>
            );

            if (b.kind === 'event') {
              const isNext = i === firstEventIdx;
              return (
                <div className="tl-row" key={`ev-${b.event.id}`}>
                  {when}
                  <div className="tl-line tl-line-event">
                    <span className="tl-flag" />
                  </div>
                  <div className="tl-body">
                    <div className={'ev-anchor' + (isNext ? ' ev-next' : '')}>
                      <div className="ev-anchor-h">
                        {/* The event's NAME is its identity — a touring series
                            runs the same event across several tracks, so the
                            title headlines and the track is a detail below. */}
                        <div className="ev-anchor-track">{b.event.name}</div>
                        {isNext && <span className="next-badge">Next up</span>}
                        {/* Owner-or-crew, planned events only — an event that has
                            run is history and the API refuses to edit it. A
                            planned event whose date has passed leads with "Record
                            result", since logging what ran is the obvious next
                            step; both open the same sheet (the record form is at
                            the top of it). */}
                        {canLogMaintenance && onEditEvent && b.event.status === 'planned' && (
                          b.event.date < today() ? (
                            <button className="ev-record" onClick={() => onEditEvent(b.event)}>
                              <Gauge size={12} /> Record result
                            </button>
                          ) : (
                            <button className="ev-edit" onClick={() => onEditEvent(b.event)} aria-label="Edit event">
                              <Pencil size={13} />
                            </button>
                          )
                        )}
                      </div>
                      <div className="ev-anchor-meta">
                        {/* Track only when there is one — a multi-track event has none. */}
                        {b.event.track && (
                          <>
                            {b.event.track}
                            <i>·</i>
                          </>
                        )}
                        {cars.find((c) => c.id === b.event.carId)?.name ?? 'Car'}
                        <i>·</i>est {b.event.estPasses} passes
                        {b.event.estMiles > 0 ? ' · ' + b.event.estMiles + ' mi' : ''}
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            if (b.kind === 'maint') {
              const m = b.occurrence;
              const repeat = !m.firm;
              return (
                <div className="tl-row" key={`mt-${m.itemId}-${m.occ}`}>
                  {when}
                  <div className={'tl-line tick-proj' + (repeat ? ' tl-line-repeat' : '')}>
                    <span className={'tl-node' + (repeat ? ' tl-node-hollow' : '')} />
                  </div>
                  <div className="tl-body">
                    <div className={'tl-card tl-card-proj' + (repeat ? ' tl-card-repeat' : '')}>
                      <div className="tlc-top">
                        <span className="tlc-name">{m.itemName}</span>
                        <MaintTag occurrence={m} plan={planFor(m.itemId)} />
                        {m.total > 1 && (
                          <span className="occ">
                            #{m.occ} of {m.total}
                          </span>
                        )}
                      </div>
                      <div className="tlc-meta">
                        <span>{m.who}</span>
                        <i>·</i>
                        <span>every {m.label}</span>
                      </div>
                      {repeat && (
                        <div className="repeat-note">
                          <CircleDashed size={11} /> Estimated — assumes serviced at the prior tile
                          {m.atPass !== null && ` · ≈ pass ${m.atPass}/${m.ofPasses}`}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div className="tl-row" key={`ct-${b.cert.itemId}`}>
                {when}
                <div className="tl-line tick-cert">
                  <span className="tl-node" />
                </div>
                <div className="tl-body">
                  <div className="tl-card tl-card-cert">
                    <div className="tlc-top">
                      <span className="tlc-name">{b.cert.itemName}</span>
                      <span className="exact">exact</span>
                    </div>
                    <div className="tlc-meta">
                      <span>{b.cert.who}</span>
                      <i>·</i>
                      <span>{b.cert.why}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {inspections.length > 0 && (
        <>
          <div className="row-h">
            <h2 className="h2">Inspect by hand</h2>
            <span className="row-sub">no metric · on a window</span>
          </div>
          <div className="inspect-grid">
            {inspections.map((inspection) => (
              <InspectCard
                key={inspection.itemId}
                inspection={inspection}
                canLog={canLogMaintenance}
                isPending={markChecked.isPending}
                onMarkChecked={() => markChecked.mutate({ itemId: inspection.itemId, checkedAt: today() })}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The tag on a service tile: what kind of crossing this is.
 *
 * A repeat says so first — its date is an estimate resting on an estimate, and
 * that outranks anything else it might say. A mid-event crossing shows the
 * user's own before/during pick. Everything else is simply "at the event".
 */
function MaintTag({ occurrence, plan }: { occurrence: TimelineOccurrence; plan: string }) {
  if (!occurrence.firm) {
    return (
      <span className="tag-repeat">
        <Repeat size={9} /> Repeat · est
      </span>
    );
  }

  if (occurrence.choice) {
    return plan === 'during' ? (
      <span className="tag-during">
        During · pass {occurrence.atPass}/{occurrence.ofPasses}
      </span>
    ) : (
      <span className="tag-before">Before</span>
    );
  }

  if (occurrence.atPass !== null) {
    return (
      <span className="tag-at">
        At event · pass {occurrence.atPass}/{occurrence.ofPasses}
      </span>
    );
  }

  // A time-driven crossing lands on a date, not inside an event.
  return <span className="tag-at">Date-driven</span>;
}

// ---------------------------------------------------------------------------
// History — the all-time record (the timeline's backward-looking twin)
// ---------------------------------------------------------------------------

function HistoryScreen({
  garageId,
  mode,
  onMode,
  cars,
  filter,
  onFilter,
}: {
  garageId: string | undefined;
  mode: TimelineMode;
  onMode: (mode: TimelineMode) => void;
  cars: CarOption[];
  filter: string;
  onFilter: (filter: string) => void;
}) {
  const query = useHistory(garageId);

  return (
    <QueryState query={query}>
      {(data) => <HistoryView data={data} mode={mode} onMode={onMode} cars={cars} filter={filter} onFilter={onFilter} />}
    </QueryState>
  );
}

type HistoryBlock =
  | { date: string; kind: 'event'; carId: string | null; event: HistoryEvent }
  | { date: string; kind: 'service'; carId: string | null; service: HistoryService };

// Newest first — history reads back from today. On a shared date the event comes
// before the services around it, the mirror of the forecast's tie-break: a
// service that landed on a race day only makes sense once the race is in view.
const HKIND_ORDER: Record<HistoryBlock['kind'], number> = { event: 0, service: 1 };

function toHistoryBlocks(data: HistoryResponse): HistoryBlock[] {
  const blocks: HistoryBlock[] = [
    ...data.events.map((event) => ({ date: event.date, kind: 'event' as const, carId: event.carId, event })),
    ...data.services.map((service) => ({
      date: service.servicedAt,
      kind: 'service' as const,
      carId: service.carId,
      service,
    })),
  ];
  return blocks.sort((a, b) => (a.date > b.date ? -1 : a.date < b.date ? 1 : HKIND_ORDER[a.kind] - HKIND_ORDER[b.kind]));
}

export function HistoryView({
  data,
  mode,
  onMode,
  cars,
  filter,
  onFilter,
}: {
  data: HistoryResponse;
  mode?: TimelineMode;
  onMode?: (mode: TimelineMode) => void;
  cars: CarOption[];
  filter: string;
  onFilter: (filter: string) => void;
}) {
  // A per-car filter drops driver-gear services (they hang off no car); "All"
  // keeps them. Same chips as the forecast, so the choice carries across tabs.
  const blocks = toHistoryBlocks(data).filter((b) => filter === 'all' || b.carId === filter);
  const showOwner = filter === 'all';

  return (
    <div className="screen">
      {onMode ? <TimelineTabs mode={mode ?? 'history'} onMode={onMode} /> : <Eyebrow>History</Eyebrow>}
      <div className="row-h">
        <h1 className="h1">History</h1>
      </div>
      <p className="lede">
        The all-time record — every event that ran and every service logged, newest first. This is fact, not forecast:
        nothing here is estimated, except an event that has already passed but whose actual numbers were never recorded.
      </p>

      <CarFilter cars={cars} filter={filter} onFilter={onFilter} />

      {blocks.length === 0 ? (
        <Empty
          title="No history yet"
          hint="Once you record an event's result or log a service, it lands here."
        />
      ) : (
        <div className="tl">
          {blocks.map((b) =>
            b.kind === 'event' ? (
              <HistoryEventRow key={`hev-${b.event.id}`} event={b.event} showCar={showOwner} />
            ) : (
              <HistoryServiceRow key={`hsv-${b.service.id}`} service={b.service} showOwner={showOwner} />
            ),
          )}
        </div>
      )}
    </div>
  );
}

/** The date rail on a history row — month/day and the year it fell in. */
function When({ date }: { date: string }) {
  return (
    <div className="tl-when">
      <b>{fmtShort(date)}</b>
      <span>{date.slice(0, 4)}</span>
    </div>
  );
}

function HistoryEventRow({ event, showCar }: { event: HistoryEvent; showCar: boolean }) {
  return (
    <div className="tl-row">
      <When date={event.date} />
      <div className="tl-line tl-line-event">
        <span className="tl-flag" />
      </div>
      <div className="tl-body">
        <div className="hist-card hist-card-event">
          <div className="tlc-top">
            <span className="tlc-name">{event.name}</span>
            {event.estimated ? <span className="tag-repeat">Not recorded</span> : <span className="hist-ran">Ran</span>}
          </div>
          <div className="tlc-meta">
            {event.track && (
              <>
                <span>{event.track}</span>
                <i>·</i>
              </>
            )}
            {showCar && (
              <>
                <span>{event.carName}</span>
                <i>·</i>
              </>
            )}
            {event.driverName && (
              <>
                <span>{event.driverName}</span>
                <i>·</i>
              </>
            )}
            <span>
              {event.passes} passes
              {event.miles > 0 ? ` · ${event.miles.toLocaleString('en-US')} mi` : ''}
              {event.estimated ? ' · est' : ''}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function HistoryServiceRow({ service, showOwner }: { service: HistoryService; showOwner: boolean }) {
  const usage = [
    service.atPasses !== null ? `${service.atPasses} passes` : null,
    service.atMiles !== null ? `${service.atMiles.toLocaleString('en-US')} mi` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="tl-row">
      <When date={service.servicedAt} />
      <div className="tl-line tick-done">
        <span className="tl-node" />
      </div>
      <div className="tl-body">
        <div className="hist-card">
          <div className="tlc-top">
            <span className="tlc-name">{service.itemName}</span>
            <span className="hist-serviced">
              <Wrench size={9} /> Serviced
            </span>
            {service.cost !== null && (
              <span className="hist-cost">
                <DollarSign size={9} />
                {service.cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            )}
          </div>
          <div className="tlc-meta">
            {showOwner && (
              <>
                <span>{service.ownerLabel}</span>
                <i>·</i>
              </>
            )}
            <span>by {service.byUserName}</span>
            {usage && (
              <>
                <i>·</i>
                <span>at {usage}</span>
              </>
            )}
            {service.eventName && (
              <>
                <i>·</i>
                <span>{service.eventName}</span>
              </>
            )}
          </div>
          {service.notes && <div className="hist-note">{service.notes}</div>}
        </div>
      </div>
    </div>
  );
}
